import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Box, TextField, Typography, Button } from "@material-ui/core";
import { useTranslation } from "@/langs/useTranslation";
import HasPermission from "@/components/commons/HasPermission";
import { useFormik } from "formik";
import ConfigDetail from "./AndroidConfig/ConfigDetail";
import styles from "./index.module.less";
import {
  addProfileConfig,
  getProfileConfigHistoryInfo,
  getProfileConfigInfo,
  updateProfileConfig
} from "@/apis/apiCollection";
import { useHistory } from "react-router-dom";
import { toast } from "@/apis/request";
import SystemConfig, {
  initValues as systemConfigInitValues
} from "./WindowsConfig/SystemConfig";
import WallPaperSetting, {
  initValues as wallPaperSettingInitValues,
  wallPaperSettingName
} from "./AndroidConfig/WallPaper";
import SystemControl, {
  initValues as systemControlInitValues,
  systemControlName
} from "./WindowsConfig/SystemControl";
import AppConfig, {
  initValues as appConfigInitValues
} from "./WindowsConfig/AppConfig";
import AppBlockList, {
  initValues as appBlockListInitValues
} from "./WindowsConfig/AppBlockList";
import AppWhiteBlockList, {
  initValues as appWhiteBlockListInitValues
} from "./WindowsConfig/AppWhiteBlockList";
import DeployFiles, {
  initValues as deployFilesInitValues
} from "./AndroidConfig/DeployFiles";
import WifiConfig, {
  initValues as wifiConfigInitValues
} from "./AndroidConfig/WifiConfig";
import PeripheralManagement, {
  initValues as peripheralInitValues,
  peripheralSettingName
} from "./WindowsConfig/PeripheralManagement";
import { setValueWithDefault } from "./AndroidTemplateForm";

const TemplateForm = ({ readOnly, id, isHistory }) => {
  const { t, userLanguage } = useTranslation();
  const history = useHistory();
  const initialValues = useMemo(() => {
    return {
      templateName: "",
      configDetail: {},
      systemSetting: systemConfigInitValues,
      [wallPaperSettingName]: wallPaperSettingInitValues,
      [systemControlName]: systemControlInitValues,
      application: appConfigInitValues,
      wifi: wifiConfigInitValues,
      fileSynchronization: deployFilesInitValues,
      applicationBlacklist: appBlockListInitValues,
      applicationWhitelist: appWhiteBlockListInitValues,
      [peripheralSettingName]: peripheralInitValues
    };
  }, []);
  const routes = useMemo(
    () => [
      [t({ defaultMessage: "配置文件详情" }), ConfigDetail, "configDetail"],
      [t({ defaultMessage: "应用管理" }), AppConfig, "application"],
      [t({ defaultMessage: "系统设置" }), SystemConfig, "systemSetting"],
      [t({ defaultMessage: "Wi-Fi" }), WifiConfig, "wifi"],
      [t({ defaultMessage: "文件同步" }), DeployFiles, "fileSynchronization"],
      [t({ defaultMessage: "黑名单" }), AppBlockList, "applicationBlacklist"],
      // [t({ defaultMessage: "白名单"}), AppWhiteBlockList, "applicationWhitelist"],
      [
        t({ defaultMessage: "壁纸配置" }),
        WallPaperSetting,
        wallPaperSettingName
      ],
      [t({ defaultMessage: "功能控制" }), SystemControl, systemControlName]
      // [t({ defaultMessage: "周边管理" }), PeripheralManagement, peripheralSettingName],
    ],
    [userLanguage]
  );
  const { values, setValues, handleChange, handleSubmit, handleReset, setFieldValue } =
    useFormik({
      initialValues,
      onSubmit: () => {
        // handleReloadDataTable();
      }
    });
  const [chosenConfigIndex, setChosenConfigIndex] = useState(0);
  const SelectedConfig = routes[chosenConfigIndex][1];
  const SelectedConfigData = values[routes[chosenConfigIndex][2]];

  const handleSaveDraft = useCallback(async () => {
    if (
      !values.templateName ||
      (values.templateName &&
        values.templateName.trim &&
        !values.templateName.trim())
    ) {
      toast.error(t({ defaultMessage: "未填写模板名称" }));
      return;
    }
    if (values.wifi && values.wifi.open) {
      if (!values.wifi.wifis.length) {
        setChosenConfigIndex(3);
        toast.error(t({ defaultMessage: "请填写wifi配置" }));
        return;
      }
      if (values.wifi.wifis.some(wifi => !wifi.ssid || !wifi.password)) {
        setChosenConfigIndex(3);
        toast.error(t({ defaultMessage: "wifi配置填写不全" }));
        return;
      }
    }
    const params = {
      name: values.templateName,
      osType: "windows",
      application: JSON.stringify(values.application),
      systemSetting: JSON.stringify(values.systemSetting),
      [wallPaperSettingName]: JSON.stringify(values[wallPaperSettingName]),
      [systemControlName]: JSON.stringify(values[systemControlName]),
      wifi: JSON.stringify(values.wifi),
      fileSynchronization: JSON.stringify(values.fileSynchronization),
      applicationBlacklist: JSON.stringify(values.applicationBlacklist),
      applicationWhitelist: JSON.stringify(values.applicationWhitelist),
      [peripheralSettingName]: JSON.stringify(values[peripheralSettingName])
    };
    routes
      .filter(route => route[2] !== "configDetail")
      .forEach(route => {
        const name = route[2];
        params[name] = JSON.stringify(values[name]);
      });
    if (id) {
      await updateProfileConfig({
        configId: id,
        ...params
      });
    } else {
      await addProfileConfig(params);
    }
    history.push("/profile-config/template");
    toast.success(t({ defaultMessage: "保存成功" }));
  }, [values, id]);

  useEffect(() => {
    if (id) {
      const handleData = res => {
        // const blackList = JSON.parse(res.applicationBlacklist)
        // if (blackList.enableWhiteList === undefined) blackList.enableWhiteList = false
        // res.applicationBlacklist = JSON.stringify(blackList)
        setValues({
          templateName: res.name,
          configDetail: {
            status: res.status,
            version: res.version,
            person: res.updateUserName || res.createUserName,
            lastUpdateTime: Number(res.updatedAt)
          },
          application: setValueWithDefault(
            res.application,
            appConfigInitValues
          ),
          systemSetting: setValueWithDefault(
            res.systemSetting,
            systemConfigInitValues
          ),
          wifi: setValueWithDefault(res.wifi, wifiConfigInitValues),
          fileSynchronization: setValueWithDefault(
            res.fileSynchronization,
            deployFilesInitValues
          ),
          applicationBlacklist: setValueWithDefault(
            res.applicationBlacklist,
            appBlockListInitValues
          ),
          applicationWhitelist: setValueWithDefault(
            res.applicationWhitelist,
            appWhiteBlockListInitValues
          ),
          [systemControlName]: setValueWithDefault(
            res[systemControlName],
            systemControlInitValues
          ),
          [wallPaperSettingName]: setValueWithDefault(
            res[wallPaperSettingName],
            wallPaperSettingInitValues
          ),
          [peripheralSettingName]: setValueWithDefault(
            res[peripheralSettingName],
            peripheralInitValues
          )
        });
      };
      if (isHistory)
        getProfileConfigHistoryInfo({ configHistoryId: id }).then(handleData);
      else getProfileConfigInfo({ configId: id }).then(handleData);
    }
  }, [id]);

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", flex: 1 }}
    >
      <Box display="flex" alignItems="center">
        {isHistory ? (
          <Typography variant="h4">{values.templateName}</Typography>
        ) : (
          <>
            <Typography variant="h6" style={{ marginRight: 8 }}>
              {t({ defaultMessage: "模板名称：" })}
            </Typography>
            <TextField
              variant="outlined"
              name="templateName"
              onChange={readOnly ? () => { } : handleChange}
              style={{ width: 512, background: "#fff" }}
              value={values.templateName}
              size="small"
              inputProps={{ maxLength: 30 }}
            // readOnly={readOnly}
            // readOnly
            // disabled={readOnly}
            />
          </>
        )}
        <Box marginLeft="auto">
          {readOnly && (
            <HasPermission permission="profile_template_edit">
              <Button
                variant="contained"
                color="primary"
                style={{ marginRight: 8, minWidth: 118 }}
                onClick={() => history.push(`/profile-config/edit-template-windows/${id}`)}
              >
                {t({ defaultMessage: "编辑" })}
              </Button>
            </HasPermission>
          )}
          {!readOnly && (
            <Button
              variant="contained"
              color="primary"
              style={{ marginRight: 8, minWidth: 118 }}
              onClick={handleSaveDraft}
            >
              {t({ defaultMessage: "保存" })}
            </Button>
          )}
          {/* {!readOnly && ( */}
            <Button
              variant="outlined"
              color="primary"
              type="submit"
              style={{ marginRight: 8, minWidth: 118 }}
              onClick={() => history.push("/profile-config/template")}
            >
              {t({ defaultMessage: "取消" })}
            </Button>
          {/* )} */}
        </Box>
      </Box>
      <Box display="flex" flex={1} marginTop={3}>
        <Box
          display="flex"
          flexDirection="column"
          width={240}
          padding="22px 16px"
          sx={containerSx}
        >
          {routes.map((route, ind) => {
            const [name] = route;
            return (
              <div
                className={`${styles.templateName} ${ind === chosenConfigIndex ? styles.chosenTemplate : ""
                  }`}
                onClick={() => setChosenConfigIndex(ind)}
                key={name}
              >
                {name}
              </div>
            );
          })}
        </Box>
        <Box
          display="flex"
          flex={1}
          marginLeft={3}
          padding="32px"
          sx={containerSx}
        >
          <SelectedConfig
            values={SelectedConfigData}
            onChange={readOnly ? () => { } : handleChange}
            readOnly={readOnly}
            isHistory={isHistory}
            systemType="windows"
            setFieldValue={setFieldValue}
          />
        </Box>
      </Box>
    </form>
  );
};

const containerSx = {
  bgcolor: "#fff",
  borderRadius: 16
};

export default TemplateForm;
